(() => {
  if (window.flowcatFeaturedProductLoaded) return;
  window.flowcatFeaturedProductLoaded = true;

  const initializedSections = new WeakSet();

  const getGallery = (section) => section.flowcatFeaturedGallery || section.querySelector('.js-media-list')?.swiper;

  const keepThumbnailVisible = (thumbs, index) => {
    if (!thumbs || thumbs.destroyed) return;
    const visibleIndexes = thumbs.visibleSlidesIndexes || [];
    if (visibleIndexes.includes(index)) return;

    const slidesPerView = Number(thumbs.params.slidesPerView) || 5;
    const targetIndex = Math.max(0, index - Math.floor(slidesPerView / 2));
    thumbs.slideTo(targetIndex);
  };

  const setActiveThumbnail = (thumbs, index) => {
    if (!thumbs || thumbs.destroyed) return;
    thumbs.slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('swiper-slide-thumb-active', slideIndex === index);
      slide.setAttribute('aria-current', slideIndex === index ? 'true' : 'false');
    });
    keepThumbnailVisible(thumbs, index);
  };

  const initGallery = (section, attempt = 0) => {
    if (typeof window.Swiper === 'undefined') {
      if (attempt < 20) setTimeout(() => initGallery(section, attempt + 1), 100);
      return null;
    }

    const productMain = section.querySelector('[data-product-main]');
    const thumbsElement = productMain?.querySelector('.js-media-sublist');
    const galleryElement = productMain?.querySelector('.js-media-list');
    if (!productMain || !thumbsElement || !galleryElement) return null;

    if (galleryElement.swiper) galleryElement.swiper.destroy(true, true);
    if (thumbsElement.swiper) thumbsElement.swiper.destroy(true, true);

    const settings = productMain.dataset;
    const slideCount = galleryElement.querySelectorAll('.swiper-slide').length;
    const mobileSlides = Number(settings.slidesMobile || 1);
    const desktopSlides = Number(settings.slidesDesktop || 1);
    const mobileSpace = Number(settings.spaceMobile || 0);
    const desktopSpace = Number(settings.spaceDesktop || 0);

    const thumbs = new window.Swiper(thumbsElement, {
      slidesPerView: 5,
      spaceBetween: 8,
      direction: 'horizontal',
      freeMode: false,
      watchSlidesProgress: true,
      slideToClickedSlide: false,
      observer: true,
      observeParents: true,
      breakpoints: {
        990: { direction: 'vertical', slidesPerView: 5, spaceBetween: 8 }
      }
    });

    const gallery = new window.Swiper(galleryElement, {
      loop: settings.loop === 'true' && slideCount > desktopSlides,
      slidesPerView: mobileSlides,
      spaceBetween: mobileSpace,
      speed: 800,
      autoHeight: true,
      allowTouchMove: true,
      simulateTouch: true,
      grabCursor: slideCount > 1,
      observer: true,
      observeParents: true,
      watchOverflow: false,
      breakpoints: {
        990: { slidesPerView: desktopSlides, spaceBetween: desktopSpace }
      },
      on: {
        slideChange() {
          if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
          setActiveThumbnail(thumbs, this.realIndex);
        }
      }
    });

    section.flowcatFeaturedGallery = gallery;
    section.flowcatFeaturedThumbs = thumbs;
    setActiveThumbnail(thumbs, gallery.realIndex || 0);
    requestAnimationFrame(() => {
      thumbs.update();
      gallery.update();
    });
    return gallery;
  };

  const slideToIndex = (section, index, attempt = 0) => {
    const gallery = getGallery(section) || initGallery(section);
    if (!gallery || gallery.destroyed) {
      if (attempt >= 10) return;
      requestAnimationFrame(() => slideToIndex(section, index, attempt + 1));
      return;
    }

    if (gallery.params.loop && typeof gallery.slideToLoop === 'function') gallery.slideToLoop(index);
    else gallery.slideTo(index);
  };

  const getMediaIndex = (section, mediaId) => {
    return [...section.querySelectorAll('.product__media-subitem[data-media-sub-id]')]
      .findIndex((item) => item.dataset.mediaSubId === mediaId);
  };

  const updateVariantStatus = (picker, variants, selectedOptions) => {
    [...picker.querySelectorAll('fieldset')].forEach((fieldset, optionIndex) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const candidateOptions = [...selectedOptions];
        candidateOptions[optionIndex] = input.value;
        const isAvailable = variants.some((variant) => {
          return variant.available && variant.options.every((value, index) => value === candidateOptions[index]);
        });
        input.classList.toggle('disabled', !isAvailable);
      });
    });
  };

  const updateVariant = (section, picker) => {
    const variantsScript = picker.querySelector('script[type="application/json"]');
    if (!variantsScript) return;

    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      console.error('FlowCat featured product variants could not be parsed.', error);
      return;
    }

    const selectedOptions = [...picker.querySelectorAll('fieldset')].map((fieldset) => {
      return fieldset.querySelector('input[type="radio"]:checked')?.value;
    });
    const variant = variants.find((item) => {
      return item.options.every((value, index) => value === selectedOptions[index]);
    });

    updateVariantStatus(picker, variants, selectedOptions);

    const form = section.querySelector('product-form form');
    const variantInput = form?.querySelector('input[name="id"]');
    const button = form?.querySelector('[name="add"]');
    const buttonLabel = button?.querySelector('.button__label');
    const price = section.querySelector('[data-flowcat-featured-price]');

    if (!variant) {
      if (button) button.disabled = true;
      if (buttonLabel) buttonLabel.textContent = window.variantStrings?.unavailable || 'Unavailable';
      return;
    }

    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (price) {
      const priceValue = price.querySelector('.price-item--regular');
      const moneyFormat = price.dataset.moneyFormat || window.theme?.moneyFormat || '{{ amount }}';
      if (priceValue && typeof window.formatMoney === 'function') {
        priceValue.textContent = window.formatMoney(variant.price, moneyFormat);
      }
    }

    if (button) {
      button.disabled = !variant.available;
      button.setAttribute('aria-disabled', String(!variant.available));
    }
    if (buttonLabel) {
      buttonLabel.textContent = variant.available
        ? button.dataset.addLabel
        : (window.variantStrings?.soldOut || 'Sold Out');
    }

    if (variant.featured_media) {
      const mediaIndex = getMediaIndex(section, `${section.dataset.section}-${variant.featured_media.id}`);
      if (mediaIndex >= 0) slideToIndex(section, mediaIndex);
    }
  };

  const initSection = (section) => {
    if (!section || initializedSections.has(section)) return;
    initializedSections.add(section);

    section.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('.product__media-subitem[data-media-sub-id]');
      if (!thumbnail || !section.contains(thumbnail)) return;
      const index = getMediaIndex(section, thumbnail.dataset.mediaSubId);
      if (index >= 0) slideToIndex(section, index);
    });

    section.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const thumbnail = event.target.closest('.product__media-subitem[data-media-sub-id]');
      if (!thumbnail || !section.contains(thumbnail)) return;
      event.preventDefault();
      const index = getMediaIndex(section, thumbnail.dataset.mediaSubId);
      if (index >= 0) slideToIndex(section, index);
    });

    const picker = section.querySelector('variant-radios');
    picker?.addEventListener('change', () => updateVariant(section, picker));

    requestAnimationFrame(() => initGallery(section));
  };

  const initIn = (root = document) => {
    if (root.matches?.('[data-flowcat-featured-product]')) initSection(root);
    root.querySelectorAll?.('[data-flowcat-featured-product]').forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initIn(), { once: true });
  } else {
    initIn();
  }

  document.addEventListener('shopify:section:load', (event) => initIn(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector?.('[data-flowcat-featured-product]');
    section?.flowcatFeaturedGallery?.destroy(true, true);
    section?.flowcatFeaturedThumbs?.destroy(true, true);
  });
})();
